import { useMemo, useCallback } from 'react';
import { Layouts } from 'react-grid-layout';
import { Widget } from 'graphql-common';
import _get from 'lodash/get';
import { LocalWidget, GridLayoutItem } from './useWidgetEditor';
import { getWidgetSizeConstraints } from '../constants';
import isWidgetVisibleByPeriod from '../utils/isWidgetVisibleByPeriod';

interface UseWidgetGridProps {
  widgets: Widget[];
  localWidgets: LocalWidget[];
  localGrid: GridLayoutItem[];
  isEditMode: boolean;
}

interface UseWidgetGridReturn {
  /** Filtered widgets to display (respects edit mode and visibility period) */
  displayWidgets: (Widget | LocalWidget)[];
  /** Generated layouts for react-grid-layout */
  layouts: Layouts;
  /** Grid items built from widgets */
  widgetsGrid: GridLayoutItem[];
}

/**
 * Hook to manage widget grid layout and filtering logic.
 * Handles visibility period filtering in view mode and destroyed widget filtering in edit mode.
 */
export default function useWidgetGrid({
  widgets,
  localWidgets,
  localGrid,
  isEditMode,
}: UseWidgetGridProps): UseWidgetGridReturn {
  // Helper to check widget visibility by period
  const isWidgetVisible = useCallback(
    (widget: Widget | LocalWidget): boolean => {
      const startAt = _get(widget, 'fields.startAt') as
        | string
        | null
        | undefined;
      const endAt = _get(widget, 'fields.endAt') as string | null | undefined;
      return isWidgetVisibleByPeriod({ startAt, endAt });
    },
    [],
  );

  // Build grid from widgets' grid field with size constraints
  // In view mode, only include visible widgets (by period)
  const widgetsGrid = useMemo((): GridLayoutItem[] => {
    const widgetsToUse = isEditMode ? widgets : widgets.filter(isWidgetVisible);

    return widgetsToUse
      .filter((widget) => widget.grid)
      .map((widget) => {
        const grid = widget.grid as {
          x?: number;
          y?: number;
          w?: number;
          h?: number;
        } | null;
        const constraints = getWidgetSizeConstraints(widget.kind);
        return {
          i: widget.id,
          x: grid?.x ?? 0,
          y: grid?.y ?? 0,
          w: grid?.w ?? constraints.defaultW,
          h: grid?.h ?? constraints.defaultH,
          minW: constraints.minW,
          minH: constraints.minH,
          maxW: constraints.maxW,
          maxH: constraints.maxH,
        };
      });
  }, [widgets, isEditMode, isWidgetVisible]);

  // Generate layouts from localGrid (edit mode) or widgetsGrid (view mode)
  const layouts: Layouts = useMemo(() => {
    // In edit mode, use localGrid if it has items, otherwise fall back to widgetsGrid
    // This ensures we show correct sizes on initial edit mode entry before localGrid is populated
    const getGridItems = (): GridLayoutItem[] => {
      if (!isEditMode) return widgetsGrid;
      if (localGrid.length > 0) return localGrid;
      return widgetsGrid;
    };
    const gridItems = getGridItems();

    if (!gridItems.length) {
      // Generate default layouts from widgets if no grid is saved
      const widgetsForLayout = isEditMode
        ? localWidgets.filter((w) => !w.isDestroyed)
        : widgets;

      if (!widgetsForLayout.length) {
        return { lg: [] };
      }

      // Create default layout based on widget positions with size constraints
      const sortedWidgets = [...widgetsForLayout].sort((a, b) => {
        const posA = a.position ?? 0;
        const posB = b.position ?? 0;
        return posA - posB;
      });

      const lgLayout = sortedWidgets.map((widget, index) => {
        const id =
          'tempId' in widget
            ? widget.id || (widget as LocalWidget).tempId
            : widget.id;
        const constraints = getWidgetSizeConstraints(widget.kind);
        // Default: 4 widgets per row (each 3 cols wide in 12-col grid)
        const widgetsPerRow = 4;
        const x = (index % widgetsPerRow) * constraints.defaultW;
        const y = Math.floor(index / widgetsPerRow) * constraints.defaultH;
        return {
          i: id || '',
          x,
          y,
          w: constraints.defaultW,
          h: constraints.defaultH,
          minW: constraints.minW,
          minH: constraints.minH,
          maxW: constraints.maxW,
          maxH: constraints.maxH,
        };
      });

      return { lg: lgLayout };
    }

    return { lg: gridItems };
  }, [isEditMode, localGrid, widgetsGrid, localWidgets, widgets]);

  // Filter widgets - in edit mode show all, in view mode filter by visibility period
  const displayWidgets = useMemo(() => {
    if (isEditMode) {
      return localWidgets.filter((w) => !w.isDestroyed);
    }
    // In view mode, filter out widgets outside their visibility period
    return widgets.filter(isWidgetVisible);
  }, [isEditMode, localWidgets, widgets, isWidgetVisible]);

  return {
    displayWidgets,
    layouts,
    widgetsGrid,
  };
}
